import React, { useState } from "react";
import { format, startOfWeek, endOfWeek, subWeeks, parseISO } from "date-fns";
import { Paper } from "@mui/material";
import { ANLoadingScreen } from "@answersnow/pkg-theme";

import useGetClinicianSessionUtilization from "hooks/api/clinician/useGetClinicianSessionUtilization";
import { ModuleContainer } from "../styles";
import SessionUtilizationModule from "./SessionUtilizationModule";
import {
  type TableRow,
  PERIOD_FILTERS,
  UNIT_FILTERS,
  type PeriodFilter,
  type UnitFilter,
  type CustomDateRange,
} from "./types";

export { PERIOD_FILTERS, UNIT_FILTERS };
export type { TableRow, PeriodFilter, UnitFilter, CustomDateRange };

interface Props {
  clinicianId: string | number | undefined;
  isSmallScreen: boolean;
}

const getWeekDates = (period: Exclude<PeriodFilter, "custom">): { startDate: Date; endDate: Date } => {
  const today = new Date();
  if (period === PERIOD_FILTERS.LAST_WEEK) {
    const lastWeek = subWeeks(today, 1);
    return {
      startDate: startOfWeek(lastWeek, { weekStartsOn: 1 }),
      endDate: endOfWeek(lastWeek, { weekStartsOn: 1 }),
    };
  }
  return {
    startDate: startOfWeek(today, { weekStartsOn: 1 }),
    endDate: endOfWeek(today, { weekStartsOn: 1 }),
  };
};

const formatValue = (minutes: number, unit: UnitFilter): string =>
  unit === UNIT_FILTERS.HOURS
    ? `${(minutes / 60).toFixed(1)} hr`
    : `${minutes} min`;

const SessionUtilization: React.FC<Props> = ({ clinicianId, isSmallScreen }) => {
  const [period, setPeriod] = useState<PeriodFilter>(PERIOD_FILTERS.THIS_WEEK);
  const [unit, setUnit] = useState<UnitFilter>(UNIT_FILTERS.MINUTES);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>([null, null]);

  const getQueryDates = () => {
    if (period === PERIOD_FILTERS.CUSTOM) {
      const [start, end] = customDateRange;
      if (!start || !end) return null;
      return { startDate: format(start, "MM/dd/yyyy"), endDate: format(end, "MM/dd/yyyy") };
    }
    const { startDate, endDate } = getWeekDates(period);
    return { startDate: format(startDate, "MM/dd/yyyy"), endDate: format(endDate, "MM/dd/yyyy") };
  };

  const queryDates = getQueryDates();

  const { sessionUtilization, isSessionUtilizationLoading } = useGetClinicianSessionUtilization(
    clinicianId,
    queryDates ?? { startDate: "", endDate: "" }
  );

  const isLoading = isSessionUtilizationLoading && !!queryDates;

  if (isLoading) {
    return (
      <Paper
        component="section"
        elevation={2}
        sx={ModuleContainer}
        style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}
      >
        <ANLoadingScreen height="100%" />
      </Paper>
    );
  }

  const rows: TableRow[] = (queryDates ? sessionUtilization : []).map((row) => {
    const clientInitial = row.client_last_name ? `${row.client_last_name[0]}.` : "";
    const clientName = `${row.client_name ?? ""} ${clientInitial}`.trim();

    return {
      videoCallId: row.video_call_id,
      clientId: row.client_id,
      client: clientName,
      sessionDate: format(parseISO(row.session_date.slice(0, 10)), "MM/dd/yy"),
      scheduled: formatValue(Number(row.scheduled_minutes), unit),
      delivered: formatValue(Number(row.delivered_minutes), unit),
      billable: formatValue(Number(row.billable_minutes), unit),
      billableUnitsLost: Number(row.billable_units_lost),
    };
  });

  return (
    <SessionUtilizationModule
      rows={rows}
      period={period}
      onPeriodChange={setPeriod}
      unit={unit}
      onUnitChange={setUnit}
      customDateRange={customDateRange}
      onCustomDateChange={setCustomDateRange}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default SessionUtilization;
